import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, CircularProgress, MenuItem, TextField, Typography } from '@material-ui/core'
import {
  CheckCircleOutline as CheckIcon,
  ArrowForward as ArrowIcon,
  WarningAmberRounded as WarningIcon,
} from '@material-ui/icons'
import { fetchOncotree } from 'src/api/oncotree'
import { aiAvailable } from 'src/services/analysisStart'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import { ErrorLine, GroupLabel, MONO_SX, Muted, TextLink } from './components/ui'
import {
  SHEET_ACCEPT,
  fieldForHeader,
  matchRows,
  readSheet,
  rowValues,
  sheetFieldsFor,
  sheetFileKind,
  sheetUploads,
} from './model/sheet'
import { downloadTemplate, isTemplateHelp } from './model/template'
import { readXlsx } from './model/xlsx'
import { looseKey, normalFieldsOf, sampleFromCandidate } from './model/samples'
import { aiApplies, isTumourNormal } from './model/workflow'
import { mergeHpo, resolveHpoIds } from './model/hpo'

export { downloadTemplate }

// Sample sheet import. It replaces the samples area while open: read an Excel
// workbook, a CSV/TSV file or pasted Excel cells, check the column mapping, see
// how rows match the samples (and, given the uploads, which uploads join the
// batch), then apply. Values that do not parse are flagged on their rows. Its own
// Cancel is the way out; the panel's footer shows no buttons meanwhile.

const FOOTER_BUTTON_SX = { height: 36, px: 2, fontSize: 14 }

// The sheet's one footer stays in view while its content scrolls.
const FOOTER_SX = {
  position: 'sticky',
  bottom: 0,
  zIndex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 1.5,
  mt: 3,
  py: 1.5,
  backgroundColor: 'background.paper',
}

const ReportLine = ({ ok, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minHeight: 24 }}>
    {ok ? (
      <CheckIcon sx={{ fontSize: 17, color: 'success.main', mt: '2px' }} />
    ) : (
      <WarningIcon sx={{ fontSize: 17, color: 'text.primary', mt: '2px' }} />
    )}
    <Typography sx={{ fontSize: 14 }}>{children}</Typography>
  </Box>
)

const listNames = (names, max = 4) =>
  names.length > max ? `${names.slice(0, max).join(', ')} and ${names.length - max} more` : names.join(', ')

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

// "Sample ID" reads "sample ID" mid-sentence; "AI analysis" and "HPO terms" keep their capitals.
const midSentence = (label) => (/^[A-Z]{2}/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1))

// Resolves tumour type cells (OncoTree code or name) through the OncoTree search.
const resolveTumourTypes = async (values) => {
  const out = new Map()
  const unique = Array.from(new Set(values.map((v) => v.trim()).filter(Boolean)))
  // eslint-disable-next-line no-restricted-syntax
  for (const value of unique) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const results = await fetchOncotree(value, 10)
      const lower = value.toLowerCase()
      const match =
        results.find((r) => String(r.oncotree_code || '').toLowerCase() === lower) ||
        results.find((r) => String(r.name || '').toLowerCase() === lower)
      if (match) out.set(value, match)
    } catch (e) {
      // Reported as not found.
    }
  }
  return out
}

// A chosen file as text or bytes. Text is read as UTF-8, and again as Turkish
// Windows (what Excel's plain "CSV" saves on a Turkish PC) when UTF-8 does not fit.
const readFileAs = (file, as, encoding) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error('read failed'))
    if (as === 'buffer') reader.readAsArrayBuffer(file)
    else reader.readAsText(file, encoding)
  })

const readTextFile = async (file) => {
  const text = String((await readFileAs(file, 'text')) || '')
  if (!text.includes('\ufffd')) return text
  try {
    return String((await readFileAs(file, 'text', 'windows-1254')) || '')
  } catch (e) {
    return text
  }
}

/**
 * The two template downloads, "Excel template" and "CSV template", for the samples
 * given (none: an example row) and the batch's workflow. Links by default; soft
 * buttons with variant="button".
 */
export const TemplateChoices = ({ samples = [], workflow, variant = 'link', sx }) => {
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState('')
  const mounted = useRef(true)
  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )
  const download = async (format) => {
    setBusy(format)
    setError('')
    try {
      await downloadTemplate(format, samples, workflow)
    } catch (e) {
      if (mounted.current) setError('Could not build the Excel file. The CSV template has the same columns.')
    } finally {
      if (mounted.current) setBusy(null)
    }
  }
  const choices = [
    { format: 'xlsx', label: 'Excel template' },
    { format: 'csv', label: 'CSV template' },
  ]
  return (
    <>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: variant === 'button' ? 1 : 2, ...sx }}>
        {choices.map(({ format, label }) =>
          variant === 'button' ? (
            <ResultButton
              key={format}
              disabled={busy !== null}
              onClick={() => download(format)}
              sx={{ height: 32, px: 1.5, fontSize: 13.5 }}
            >
              {label}
            </ResultButton>
          ) : (
            <TextLink key={format} disabled={busy !== null} onClick={() => download(format)}>
              {label}
            </TextLink>
          ),
        )}
      </Box>
      {error && <ErrorLine>{error}</ErrorLine>}
    </>
  )
}

const SheetImport = ({ samples, normalCandidates = [], workflow = {}, candidates, analysed, onApply, onClose }) => {
  const inputRef = useRef(null)
  const mounted = useRef(true)
  const [source, setSource] = useState(null) // { name, sheet, sheets?, sheetIndex }
  const [mapping, setMapping] = useState([])
  const [paste, setPaste] = useState('')
  const [readError, setReadError] = useState('')
  const [reading, setReading] = useState(false)
  const [applying, setApplying] = useState(false)

  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )

  const germline = workflow.analysis !== 'somatic'
  const fields = useMemo(
    () =>
      sheetFieldsFor({
        analysis: germline ? 'germline' : 'somatic',
        tumourNormal: isTumourNormal(workflow),
        ai: germline && aiApplies({ ...workflow, analysis: 'germline' }, samples),
      }),
    [germline, workflow, samples],
  )
  const allowed = useMemo(() => new Set(fields.map((f) => f.value)), [fields])

  // Uploads a row can bring into the batch. Only when the panel passes them.
  const uploads = useMemo(
    () => (candidates ? sheetUploads(candidates, { workflow, analysed, samples }) : []),
    [candidates, analysed, workflow, samples],
  )
  const canAdd = Boolean(candidates)

  const load = (name, input, sheets = null, sheetIndex = 0) => {
    const sheet = readSheet(input)
    if (!sheet.headers.length || !sheet.rows.length) {
      setReadError('Could not find a header row and at least one sample row.')
      return
    }
    setReadError('')
    setSource({ name, sheet, sheets, sheetIndex })
    setMapping(sheet.mapping.map((field) => (field && allowed.has(field) ? field : null)))
  }

  const onFile = async (file) => {
    if (!file) return
    const kind = sheetFileKind(file.name)
    if (kind === 'xls') {
      setReadError('This is the old Excel format. Save it as .xlsx or CSV, then choose it again.')
      return
    }
    if (kind === 'other') {
      setReadError('Save the sheet as Excel (.xlsx) or CSV, or paste the cells.')
      return
    }
    setReading(true)
    setReadError('')
    try {
      if (kind === 'xlsx') {
        const sheets = (await readXlsx(await readFileAs(file, 'buffer'))).filter((sh) => !isTemplateHelp(sh))
        if (!mounted.current) return
        if (!sheets.length) {
          setReadError('This workbook has no filled worksheet.')
          return
        }
        // The first worksheet with a sample ID header, else the first with data.
        const index = Math.max(
          0,
          sheets.findIndex((sh) => readSheet(sh.rows).headers.some((h) => fieldForHeader(h) === 'sample')),
        )
        load(file.name, sheets[index].rows, sheets, index)
      } else {
        const text = await readTextFile(file)
        if (mounted.current) load(file.name, text)
      }
    } catch (e) {
      if (mounted.current) {
        setReadError(
          kind === 'xlsx'
            ? 'Could not read this workbook. Save it again in Excel, or save it as CSV.'
            : 'Could not read this file. Save it as CSV and try again.',
        )
      }
    } finally {
      if (mounted.current) setReading(false)
    }
  }

  const pickWorksheet = (index) => {
    if (!source?.sheets?.[index]) return
    load(source.name, source.sheets[index].rows, source.sheets, index)
  }

  const sampleCol = mapping.indexOf('sample')
  const report = useMemo(() => {
    if (!source || sampleCol < 0) return null
    return matchRows(source.sheet.rows, sampleCol, samples, uploads)
  }, [source, sampleCol, samples, uploads])

  // The sample each matched row fills: one in the panel, or a new one for an upload.
  const targets = useMemo(() => {
    if (!report) return []
    const byUid = new Map(samples.map((s) => [s.uid, s]))
    return [
      ...report.matches.map((m) => ({ rowIndex: m.rowIndex, uid: m.uid, sample: byUid.get(m.uid) })),
      ...report.additions.map((a) => ({
        rowIndex: a.rowIndex,
        candidate: a.candidate,
        sample: sampleFromCandidate(a.candidate),
      })),
    ].filter((t) => t.sample)
  }, [report, samples])

  // A row's values for its sample. AI set to yes where AI cannot run (a CNV or SV
  // VCF) is flagged instead.
  const evaluate = (target) => {
    const { values, errors } = rowValues(source.sheet.rows[target.rowIndex], mapping)
    if (values.ai === true && !aiAvailable({ ...workflow, analysis: 'germline' }, target.sample)) {
      delete values.ai
      errors.ai = 'AI does not run on CNV or SV VCFs'
    }
    return { values, errors }
  }

  const evaluated = useMemo(
    () => (source ? targets.map((t) => ({ ...t, ...evaluate(t) })) : []),
    // evaluate reads source, mapping and workflow, which are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targets, source, mapping, workflow],
  )

  const valueErrors = evaluated.reduce((n, t) => n + Object.keys(t.errors).length, 0)

  // The AI column in numbers, and the samples set to AI that still miss phenotype.
  const aiReport = useMemo(() => {
    if (!mapping.includes('ai') || !evaluated.length) return null
    let yes = 0
    let no = 0
    let missing = 0
    evaluated.forEach(({ sample, values }) => {
      if (values.ai === true) {
        yes += 1
        const hpo = (sample.hpo || []).length + (values.hpoIds || []).length
        const history = String(values.clinicalHistory || sample.clinicalHistory || '').trim()
        if (!hpo || !history) missing += 1
      } else if (values.ai === false) no += 1
    })
    return { yes, no, blank: evaluated.length - yes - no, missing }
  }, [mapping, evaluated])

  const setColumnField = (col, field) =>
    setMapping((prev) => prev.map((f, i) => (i === col ? field || null : f === field && field ? null : f)))

  const apply = async () => {
    if (!source || !report) return
    setApplying(true)
    let applied = false
    try {
      const hpoIds = evaluated.flatMap((t) => t.values.hpoIds || [])
      const hpoMap = hpoIds.length ? await resolveHpoIds(hpoIds) : new Map()
      const tumourValues = evaluated.map((t) => t.values.tumourType).filter(Boolean)
      const tumourMap = tumourValues.length ? await resolveTumourTypes(tumourValues) : new Map()
      const normalsByKey = new Map()
      normalCandidates.forEach((c) => {
        const k = looseKey(c.key)
        if (!normalsByKey.has(k)) normalsByKey.set(k, c)
      })
      const mappedFields = new Set(mapping.filter(Boolean))

      const patchFor = ({ sample, values, errors: rowErrors }) => {
        const errors = { ...rowErrors }
        const patch = {}
        const changed = { ...(sample.changed || {}) }
        const sheetErrors = { ...(sample.sheetErrors || {}) }
        mappedFields.forEach((field) => delete sheetErrors[field])
        ;['sex', 'age', 'onsetAge', 'notes'].forEach((field) => {
          if (values[field] !== undefined && values[field] !== sample[field]) {
            patch[field] = values[field]
            changed[field] = true
          }
        })
        if (values.alias !== undefined) patch.alias = values.alias
        if (values.clinicalHistory !== undefined) patch.clinicalHistory = values.clinicalHistory
        // The sheet's AI choice is the sample's own: it holds when the batch's
        // interpretation changes.
        if (values.ai !== undefined) {
          patch.ai = values.ai
          patch.excludeAI = !values.ai
        }
        if (values.hpoIds) {
          const found = values.hpoIds.map((id) => hpoMap.get(id)).filter(Boolean)
          const missing = values.hpoIds.filter((id) => !hpoMap.has(id))
          patch.hpo = mergeHpo(sample.hpo, found)
          if (missing.length) errors.hpo = `HPO term not found: ${missing.slice(0, 3).join(', ')}`
        }
        if (values.tumourType) {
          const node = tumourMap.get(values.tumourType.trim())
          if (node) patch.tumourType = node
          else errors.tumourType = `Tumour type not found: ${values.tumourType}`
        }
        if (values.normal) {
          const normal = normalsByKey.get(looseKey(values.normal))
          if (normal && normal.id !== sample.fileId) {
            Object.assign(patch, normalFieldsOf(normal))
            patch.normalSuggested = false
          } else errors.normal = `Normal sample not found: ${values.normal}`
        }
        Object.entries(errors).forEach(([field, text]) => {
          sheetErrors[field] = text
        })
        patch.changed = changed
        patch.sheetErrors = sheetErrors
        return patch
      }

      const updates = new Map()
      const additions = []
      evaluated.forEach((target) => {
        const patch = patchFor(target)
        if (target.candidate) additions.push({ candidate: target.candidate, patch })
        else updates.set(target.uid, patch)
      })
      const patches = [...updates.values(), ...additions.map((a) => a.patch)]
      const flagged = patches.filter((p) => Object.keys(p.sheetErrors).length).length
      applied = true
      onApply(
        updates,
        {
          name: source.name || 'Pasted cells',
          columns: mapping.filter(Boolean).length,
          matched: report.matches.length + report.additions.length,
          added: report.additions.length,
          rows: source.sheet.rows.length,
          flagged,
        },
        additions,
      )
    } finally {
      // Applying closes this view, so only a failure resets the button.
      if (!applied && mounted.current) setApplying(false)
    }
  }

  let headerTitle = 'Import a sample sheet (Excel or CSV)'
  if (source && !source.name) headerTitle = 'Match pasted columns'
  else if (source) {
    headerTitle = (
      <>
        Match columns from
        {/* The title is a flex row, which drops a plain space between its parts. */}
        <Box component="span" sx={{ ...MONO_SX, fontSize: 16, ml: 0.75 }}>
          {source.name}
        </Box>
      </>
    )
  }

  const matchedById = report ? report.matches.filter((m) => m.how === 'id').length : 0
  const matchedLoose = report ? report.matches.filter((m) => m.how === 'loose').length : 0
  const matchCount = report ? report.matches.length + report.additions.length : 0
  const worksheets = source?.sheets && source.sheets.length > 1 ? source.sheets : null

  return (
    <Box sx={{ maxWidth: 880, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Typography sx={{ fontSize: 17, fontWeight: 600, minHeight: 30, display: 'flex', alignItems: 'center', mb: 0.5 }}>
        {headerTitle}
      </Typography>
      {source && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 3, minHeight: 32 }}>
          {worksheets && (
            <TextField
              select
              size="small"
              value={source.sheetIndex}
              onChange={(e) => pickWorksheet(Number(e.target.value))}
              SelectProps={{
                renderValue: (i) => `Worksheet: ${worksheets[i]?.name || ''}`,
                inputProps: { 'aria-label': 'Worksheet' },
              }}
              sx={{ minWidth: 180, '& .MuiInputBase-root': { fontSize: 14, height: 32 } }}
            >
              {worksheets.map((sh, i) => (
                // Worksheet names are unique within a workbook.
                <MenuItem key={sh.name} value={i} sx={{ fontSize: 14 }}>
                  {sh.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Muted>
            {plural(source.sheet.rows.length, 'row', 'rows')}
            {source.sheet.headerRow > 0 ? ` · header on row ${source.sheet.headerRow + 1}` : ''}
          </Muted>
        </Box>
      )}

      {!source && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Muted>
            {canAdd && !samples.length
              ? 'Rows match uploads by sample ID and bring them into the batch with their details.'
              : 'Rows match the samples in this batch by sample ID. Blank cells change nothing.'}
          </Muted>
          <Box>
            <GroupLabel>From a file</GroupLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <ResultButton
                disabled={reading}
                onClick={() => inputRef.current && inputRef.current.click()}
                sx={{ gap: 1 }}
              >
                {reading && <CircularProgress size={14} sx={{ color: 'inherit' }} />}
                {reading ? 'Reading the sheet' : 'Choose Excel or CSV file'}
              </ResultButton>
              <TemplateChoices samples={samples} workflow={workflow} />
            </Box>
            <input
              ref={inputRef}
              type="file"
              accept={SHEET_ACCEPT}
              data-testid="sheet-file-input"
              style={{ display: 'none' }}
              onChange={(e) => {
                onFile(e.target.files && e.target.files[0])
                e.target.value = ''
              }}
            />
          </Box>
          <Box>
            <GroupLabel>Paste from Excel</GroupLabel>
            <TextField
              multiline
              minRows={6}
              maxRows={14}
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              placeholder="Copy the cells in Excel, header row included, and paste them here"
              sx={{ width: '100%', '& textarea': { ...MONO_SX, fontSize: 13, lineHeight: 1.5 } }}
            />
            <Box sx={{ mt: 1.5 }}>
              <ResultButton disabled={!paste.trim()} onClick={() => load(null, paste)}>
                Read pasted cells
              </ResultButton>
            </Box>
          </Box>
          {readError && <ErrorLine sx={{ mt: 0 }}>{readError}</ErrorLine>}
          <Muted>
            Columns read: {fields.map((f) => midSentence(f.label)).join(', ')}. English and Turkish headers both work.
          </Muted>
        </Box>
      )}

      {!source && (
        <>
          <Box sx={{ flex: 1 }} />
          <Box sx={FOOTER_SX}>
            <ResultButton onClick={onClose} sx={FOOTER_BUTTON_SX}>
              Cancel
            </ResultButton>
          </Box>
        </>
      )}

      {source && (
        <>
          {readError && <ErrorLine sx={{ mt: 0, mb: 2 }}>{readError}</ErrorLine>}
          <GroupLabel>Columns</GroupLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {source.sheet.headers.map((header, col) => {
              const examples = source.sheet.rows
                .map((r) => r[col])
                .filter(Boolean)
                .slice(0, 3)
              return (
                <Box
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${header}-${col}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 200px) minmax(0, 1fr) 20px 220px 20px',
                    alignItems: 'center',
                    columnGap: 2,
                    minHeight: 48,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap title={header}>
                    {header}
                  </Typography>
                  <Typography sx={{ ...MONO_SX, fontSize: 12.5, color: 'text.secondary' }} noWrap>
                    {examples.join(', ')}
                  </Typography>
                  <ArrowIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <TextField
                    select
                    size="small"
                    value={mapping[col] || ''}
                    onChange={(e) => setColumnField(col, e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    sx={{ '& .MuiInputBase-root': { fontSize: 14 } }}
                  >
                    <MenuItem value="" sx={{ fontSize: 14 }}>
                      Don&apos;t import
                    </MenuItem>
                    {fields.map((f) => (
                      <MenuItem key={f.value} value={f.value} sx={{ fontSize: 14 }}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {mapping[col] ? (
                    <CheckIcon sx={{ fontSize: 17, color: 'success.main' }} titleAccess="Mapped" />
                  ) : (
                    <span />
                  )}
                </Box>
              )
            })}
          </Box>
          {sampleCol < 0 && <ErrorLine>Pick the column that holds the sample ID.</ErrorLine>}

          {report && (
            <Box sx={{ mt: 4 }}>
              <GroupLabel>Match report</GroupLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {matchedById > 0 && (
                  <ReportLine ok>{plural(matchedById, 'row', 'rows')} matched by sample ID</ReportLine>
                )}
                {matchedLoose > 0 && (
                  <ReportLine ok>
                    {plural(matchedLoose, 'row', 'rows')} matched after ignoring case and separators
                  </ReportLine>
                )}
                {report.additions.length > 0 && (
                  <ReportLine ok>
                    {plural(report.additions.length, 'row matches an upload', 'rows match uploads')} not in the batch.{' '}
                    {report.additions.length === 1 ? 'It joins' : 'They join'} the batch
                  </ReportLine>
                )}
                {matchCount === 0 && (
                  <ReportLine>
                    {canAdd ? 'No row matches a sample or an upload' : 'No row matches a sample in this batch'}
                  </ReportLine>
                )}
                {report.unmatchedRows.length > 0 && (
                  <ReportLine>
                    {plural(report.unmatchedRows.length, 'row has', 'rows have')}{' '}
                    {canAdd ? 'no sample or upload' : 'no sample here'}:{' '}
                    {listNames(report.unmatchedRows.map((r) => r.id))}
                  </ReportLine>
                )}
                {report.samplesWithoutRow.length > 0 ? (
                  <ReportLine>
                    {plural(report.samplesWithoutRow.length, 'sample has', 'samples have')} no row:{' '}
                    {listNames(report.samplesWithoutRow)}
                  </ReportLine>
                ) : (
                  report.matches.length > 0 && <ReportLine ok>Every sample has a row</ReportLine>
                )}
                {report.duplicateIds.length > 0 && (
                  <ReportLine>
                    Duplicate sample ID in sheet: {listNames(report.duplicateIds)}. The first row is used
                  </ReportLine>
                )}
                {aiReport && aiReport.yes + aiReport.no > 0 && (
                  <ReportLine ok>
                    AI analysis:{' '}
                    {[
                      aiReport.yes ? `${aiReport.yes} yes` : '',
                      aiReport.no ? `${aiReport.no} no` : '',
                      aiReport.blank
                        ? `${aiReport.blank} blank ${aiReport.blank === 1 ? 'follows' : 'follow'} the batch`
                        : '',
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </ReportLine>
                )}
                {aiReport && aiReport.missing > 0 && (
                  <ReportLine>
                    {plural(aiReport.missing, 'sample', 'samples')} set to AI{' '}
                    {aiReport.missing === 1 ? 'needs' : 'need'} HPO terms and clinical history
                  </ReportLine>
                )}
                {valueErrors > 0 && (
                  <ReportLine>
                    {plural(valueErrors, 'value needs', 'values need')} a fix after import. They are flagged on their
                    rows
                  </ReportLine>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ flex: 1 }} />
          <Box sx={FOOTER_SX}>
            <TextLink
              tone="muted"
              onClick={() => {
                setSource(null)
                setMapping([])
                setReadError('')
              }}
              sx={{ mr: 'auto' }}
            >
              Use another sheet
            </TextLink>
            <ResultButton onClick={onClose} sx={FOOTER_BUTTON_SX}>
              Cancel
            </ResultButton>
            <ResultButton
              primary
              disabled={!report || !matchCount || applying}
              onClick={apply}
              sx={{ ...FOOTER_BUTTON_SX, gap: 1 }}
            >
              {applying && <CircularProgress size={14} sx={{ color: 'inherit' }} />}
              {applying ? 'Reading HPO and tumour types' : 'Apply sheet'}
            </ResultButton>
          </Box>
        </>
      )}
    </Box>
  )
}

export default SheetImport
